import { CATEGORY_DONUT_MAX, METRIC } from '@/services/directOrderService';
import { formatMonthLabel, formatMetric } from '@/utils/core/format';
import { CHART_FONT_FAMILY } from '@/utils/agGrid/agCharts';
import { CATEGORY_PALETTE } from '@/utils/ui/palette';

// ag-charts option builders for the direct-order screen. Pure functions returning plain option
// objects — no chart instance, no DOM — so the series/axis decisions are unit-testable and the
// component is left with nothing but `AgCharts.create()`.

// The two charts drawn per metric, each in its own collapsible block on the detail screen.
export const CHART_KIND = {
	EVOLUTION: 'evolution',
	DISTRIBUTION: 'distribution',
};

export const SERIES_COLORS = {
	ordered: '#1c79bb',
	delivered: '#4CAF50',
	returned: '#E7003E',
};

// Ranked-bar palette for the distribution chart, reused cyclically. Deliberately not the 3 series
// colours above: a category is not a Commandé/Validé/Retours state and must not read as one.
export { CATEGORY_PALETTE };

/**
 * The distribution rows as the mockup renders them, in both its forms: the legend beside the donut
 * and the ranked bar list that replaces it past six categories. One builder for both so the colour
 * of a category is the same whichever form is on screen, and the same as the donut slice.
 */
export function buildCategoryRows(rows, { metric = METRIC.VALUE, unclassifiedLabel = 'N/A' } = {}) {
	const list = rows ?? [];
	const total = list.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
	const max = list.reduce((peak, row) => Math.max(peak, Number(row.value) || 0), 0);

	return list.map((row, index) => ({
		key: row.id ?? `unclassified-${index}`,
		label: row.label ?? unclassifiedLabel,
		value: row.value,
		formatted: formatMetric(row.value, metric),
		// Share of the whole, for the "· 22 %" note; `max` drives the bar length so the leading
		// category fills its track rather than leaving every bar short.
		share: total === 0 ? null : row.value / total,
		ratio: max === 0 ? 0 : row.value / max,
		color: CATEGORY_PALETTE[index % CATEGORY_PALETTE.length],
	}));
}

/**
 * Monthly evolution (RG-15): Commandé and Validé above zero, Retours below it.
 *
 * Returns are negated here rather than in the aggregation layer, so the same aggregate can also feed
 * a tooltip or a table without having to be un-negated first. The tooltip renders the magnitude back
 * as a positive figure — a "-1 200 €" return would read as a correction, not as a return.
 *
 * `compact` is the home widget's rendering: no legend, each value printed above its bar instead of a
 * y-axis to read it against, and bars widened to carry that label (ag-charts' default gaps leave
 * slivers on a widget this narrow).
 */
export function buildEvolutionOptions(series, { metric = METRIC.VALUE, showReturns = false, labels = {}, compact = false, hideYAxis = false, onBarClick = null } = {}) {
	const data = (series ?? []).map((month) => ({
		month: formatMonthLabel(month.key),
		ordered: month.ordered[metric],
		delivered: month.delivered[metric],
		returned: -month.returned[metric],
	}));

	// On the narrow mobile chart there is no room for a floating tooltip or a reserved y-axis label
	// column, so a tap has to surface the value some other way — the caller renders it as a chip.
	// ag-charts' `nodeClick` datum carries the un-negated returns value, so the callback gets the
	// magnitude back rather than the chart's internal sign convention.
	const notify = (yKey, label) => (event) => {
		if (!onBarClick) return;
		const value = yKey === 'returned' ? Math.abs(event.datum.returned) : event.datum[yKey];
		onBarClick({ month: event.datum.month, label, value, formatted: formatMetric(value, metric) });
	};

	// `outside-end` is the far end from zero, so the returns label lands under its negative bar rather
	// than across the axis. An empty month gets no label: a row of "0 €" would read as data.
	const valueLabel = (yKey) => (compact ? { label: { enabled: true, placement: 'outside-end', fontSize: 10, fontFamily: CHART_FONT_FAMILY, formatter: ({ datum }) => (datum[yKey] ? formatMetric(Math.abs(datum[yKey]), metric, { compact: true }) : '') } } : {});

	const chartSeries = [
		{ type: 'bar', xKey: 'month', yKey: 'ordered', yName: labels.ordered ?? 'Commandé', fill: SERIES_COLORS.ordered, ...valueLabel('ordered'), tooltip: { renderer: ({ datum }) => ({ content: formatMetric(datum.ordered, metric) }) }, listeners: { seriesNodeClick: notify('ordered', labels.ordered ?? 'Commandé') } },
		{ type: 'bar', xKey: 'month', yKey: 'delivered', yName: labels.delivered ?? 'Validé', fill: SERIES_COLORS.delivered, ...valueLabel('delivered'), tooltip: { renderer: ({ datum }) => ({ content: formatMetric(datum.delivered, metric) }) }, listeners: { seriesNodeClick: notify('delivered', labels.delivered ?? 'Validé') } },
	];

	if (showReturns) {
		chartSeries.push({ type: 'bar', xKey: 'month', yKey: 'returned', yName: labels.returned ?? 'Retours', fill: SERIES_COLORS.returned, ...valueLabel('returned'), tooltip: { renderer: ({ datum }) => ({ content: formatMetric(Math.abs(datum.returned), metric) }) }, listeners: { seriesNodeClick: notify('returned', labels.returned ?? 'Retours') } });
	}

	const bareYAxis = hideYAxis || compact;

	// The compact month label is set on two lines ("avr." over "26") up front: left to itself ag-charts
	// either tilts it or drops every other month as soon as two neighbours could touch.
	const compactXAxis = { paddingInner: 0.1, paddingOuter: 0.05, groupPaddingInner: 0.04, label: { autoRotate: false, formatter: ({ value }) => String(value).replace(' ', '\n') } };

	return {
		data,
		series: chartSeries,
		// ag-charts 13 takes `axes` as a dictionary keyed by axis name (x / y), not as the array the
		// older API used. An array is not an error, it is silently dropped with a console warning —
		// which costs the axis label formatters, so the amounts lose their currency.
		axes: {
			x: { type: 'category', position: 'bottom', ...(compact ? compactXAxis : {}) },
			y: { type: 'number', position: 'left', label: { enabled: !bareYAxis, formatter: ({ value }) => formatMetric(Math.abs(value), metric) } },
		},
		legend: { enabled: !compact, position: 'top', item: { paddingX: 16 } },
		// Hiding the y-axis label reclaims its reserved left gutter; the default right padding is sized
		// for a laptop viewport and left a bare mobile chart with dead space on both sides.
		padding: bareYAxis ? { top: 8, right: 8, bottom: 8, left: 8 } : undefined,
	};
}

/**
 * Category distribution (RG-16), donut form — used up to CATEGORY_DONUT_MAX categories.
 *
 * Past that count CL-10 asks for a ranked list instead, which is not a chart at all in the mockup
 * but a row per category with its own progress bar: see CategoryBreakdownList, fed by the same
 * buildCategoryRows() so the two forms agree on colours, shares and ordering.
 */
export function buildDistributionOptions(rows, { metric = METRIC.VALUE, unclassifiedLabel = 'N/A', totalLabel = 'Total' } = {}) {
	const data = buildCategoryRows(rows, { metric, unclassifiedLabel });
	const total = data.reduce((sum, row) => sum + (Number(row.value) || 0), 0);

	return {
		data,
		series: [
			{
				type: 'donut',
				angleKey: 'value',
				legendItemKey: 'label',
				fills: data.map((row) => row.color),
				innerRadiusRatio: 0.6,
				// The total sits in the hole, as the mockup has it. Callout labels are off and the
				// built-in legend disabled: the breakdown is rendered beside the chart instead, where
				// it can carry each category's amount and share rather than just its name.
				innerLabels: [
					{ text: totalLabel, fontSize: 12, color: '#6b7280', fontFamily: CHART_FONT_FAMILY },
					{ text: formatMetric(total, metric), fontSize: 18, fontWeight: 'bold', fontFamily: CHART_FONT_FAMILY },
				],
				calloutLabel: { enabled: false },
				tooltip: { renderer: ({ datum }) => ({ title: datum.label, content: formatMetric(datum.value, metric) }) },
			},
		],
		legend: { enabled: false },
	};
}

export function isDistributionDonut(rows) {
	return (rows?.length ?? 0) <= CATEGORY_DONUT_MAX;
}
